import { ID } from '@adornis/baseql/baseqlTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { allocateCounterNumber } from '@adornis/counter/server/counter-allocator.js';
import { Recipient } from '@adornis/mails/entities/Recipient.js';
import { AdornisUser, registerUser } from '@adornis/users/db/a-user.js';
import { assignRoleToUser } from '@adornis/users/server/roles.js';
import { html } from 'lit';
import { CounterNames } from '../../db/enums/counter-names.js';
import { getIncrementingDebitorennummer } from '../../debitor-numbers/server/getIncrementingDebitorennummer.js';
import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import {
  checkPermissionMiddleware,
  checkPermissionMiddlewareFunction,
} from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission, Role } from '../../permissions/roles.js';
import { getGlobalAdminRecipient, makeMartinusEmailTemplate } from '../../server/mailer.js';
import { getMartinusMailer } from '@martinus/mails/server/initMartinusMailer.js';
import { mongo } from '../../server/mongo.js';
import { MitarbeiterDaten } from '../employees/entities/mitarbeiter-daten.js';
import { Person } from '../entities/person.js';
import { sendInitMail, sendInitMailForPerson } from '../server/sendInitMailForPerson.js';

// TODO move custom endpoints here. Beware of permission

export const resendInitMail = registerMutation({
  operationName: 'resendInitMail',
  type: () => Boolean,
  params: [{ type: () => String, name: 'personID' }],
  middlewares: [checkPermissionMiddleware(Permission.RESEND_INVITE)],
  resolve: (personID: string) => async () => {
    await sendInitMailForPerson(personID);

    return true;
  },
});

export const createUser = registerMutation({
  operationName: 'createUser',
  type: () => String,
  params: [
    { type: () => String, name: 'email' },
    { type: () => String, name: 'schulID' },
    { type: () => String, name: 'role' },
    { type: () => String, name: 'vorname' },
    { type: () => String, name: 'nachname' },
  ],
  // the permission is checked against the school the user is created at, so a schulleitung cannot create
  // users at a foreign school. Roles carrying a GLOBAL context (admin, global psb) still pass for every school.
  middlewares: [
    checkPermissionMiddlewareFunction((user, args) => user.hasPermission(Permission.CREATE_USER, args.schulID)),
  ],
  resolve: (email: string, schulID: string, role: string, vorname: string, nachname: string) => async () => {
    // usernames must be unique but emails may be shared, so the login handle is a fresh debitorennummer
    // (same scheme as the person import) instead of the email.
    const debitorennummer = await getIncrementingDebitorennummer();
    const newPersonID = await registerUser({
      userClass: Person,
      username: debitorennummer,
      email,
      additionalFields: { debitorennummer },
    });
    if (!newPersonID) throw new Error(`Error while creating SGUser during Neueinstellung-process.`);
    await assignRoleToUser(newPersonID, role, [schulID], AdornisUser);

    const userCol = await getRawCollection<PartialEntityData<Person>>(Person._collectionName);
    await userCol.updateOne(
      { _id: newPersonID },
      { $set: { 'profilDaten.stammdaten.vorname': vorname, 'profilDaten.stammdaten.nachname': nachname } },
    );

    if (role === Role.SCHUELER) return newPersonID;

    const newMitarbeiter = await getByID(
      Person,
      newPersonID,
    )({
      email: 1,
      detailUrl: 1,
      roles: { name: 1, contexts: 1 },
      debitorennummer: 1,
      mitarbeiterDaten: { personalNr: 1 },
    });
    if (!newMitarbeiter) throw new Error('Newly created User cannot be found!');

    const newMitarbeiterDaten = new MitarbeiterDaten({});

    // @ts-expect-error type mismatch due to narrowed and simple entity
    await userCol.updateOne({ _id: newPersonID }, { $set: { mitarbeiterDaten: newMitarbeiterDaten.toJSON() } });

    // send the new create bewerber a mail to login
    await sendInitMail(newPersonID, schulID, email, debitorennummer, true);

    return newPersonID;
  },
});

export const createPersonalNrForUser = registerMutation({
  operationName: 'createPersonalNrForUser',
  type: () => String,
  params: [{ type: () => ID, name: 'personID' }],
  middlewares: [checkPermissionMiddleware(Permission.AG_SEKTION_W)],
  resolve: (personID: string) => async () => {
    const userCol = await getCollection(Person._class);

    // check if already has personal nr. Do not fail, just give it back.
    const user = await getByID(Person, personID)({ mitarbeiterDaten: { personalNr: 1 } });

    if (user?.mitarbeiterDaten?.personalNr) return user.mitarbeiterDaten.personalNr;

    let newNr = '';
    let foundUsers: any[] = [];
    do {
      foundUsers = [];
      newNr = (
        await mongo().withTransaction(session => allocateCounterNumber(mongo(), CounterNames.PERSONAL_NR, session))
      ).toString();
      foundUsers = await userCol.find({ 'mitarbeiterDaten.personalNr': newNr }, { projection: { _id: 1 } }).toArray();
    } while (foundUsers.length > 0);

    await userCol.updateOne({ _id: personID }, { $set: { 'mitarbeiterDaten.personalNr': newNr } });

    return newNr;
  },
});

const MAX_DESCRIPTION_LENGTH = 5_000;
const MAX_DIAGNOSTICS_LENGTH = 256 * 1024;

const cap = (value: string, maxLength: number) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}\n… (gekürzt)` : value;

/**
 * Lets a logged in user report a problem. The client collects browser info and the recorded console
 * (see `console-recorder.ts`), shows all of it to the user, and sends it along with their description.
 * The whole report goes to every admin as a text attachment.
 *
 * ponytail: no throttling - any logged in user can trigger a mail to all admins. Same open flank as
 * `sendAmbiguousAccountsMail`; the upgrade path is a per-userID cooldown.
 */
export const reportProblem = registerMutation({
  operationName: 'reportProblem',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [
    { type: () => String, name: 'description' },
    { type: () => String, name: 'diagnostics' },
  ],
  resolve: (description: string, diagnostics: string) => async () => {
    if (context.serverContext || !context.userID) throw new Error('Problemmeldungen sind nur angemeldet möglich.');

    const trimmedDescription = description?.trim();
    if (!trimmedDescription) throw new Error('Bitte beschreiben Sie das Problem.');

    const reporter = await getByID(
      Person,
      context.userID,
    )({
      _id: 1,
      email: 1,
      username: 1,
      profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
    });
    if (!reporter) throw new Error('Die meldende Person konnte nicht gefunden werden.');

    const reportedAt = new Date();
    const reporterName =
      [reporter.profilDaten?.stammdaten?.vorname, reporter.profilDaten?.stammdaten?.nachname]
        .filter(Boolean)
        .join(' ') || reporter.username;
    const cappedDescription = cap(trimmedDescription, MAX_DESCRIPTION_LENGTH);

    const report = [
      `Problemmeldung von ${reporterName} <${reporter.email}> (${reporter._id})`,
      `Gemeldet am: ${reportedAt.toISOString()}`,
      '',
      'Beschreibung:',
      cappedDescription,
      '',
      cap(diagnostics?.trim() ?? '', MAX_DIAGNOSTICS_LENGTH),
    ].join('\n');

    const recipients = await getGlobalAdminRecipient();

    const message = {
      subject: `Problemmeldung – ${reporterName}`,
      html: await makeMartinusEmailTemplate('Problemmeldung', [
        html`<b>${reporterName}</b> &lt;${reporter.email}&gt; hat ein Problem im Portal gemeldet.`,
        html`<b>Beschreibung:</b>`,
        html`<pre style="white-space: pre-wrap">${cappedDescription}</pre>`,
        `Die technischen Angaben (Browser, Version, Konsolenprotokoll) finden Sie im Anhang.`,
      ]),
      attachments: [
        {
          // no colons in the file name - they break attachments on windows clients
          filename: `problembericht-${reporter._id}-${reportedAt.toISOString().replace(/[:.]/g, '-')}.txt`,
          content: report,
          contentType: 'text/plain; charset=utf-8',
        },
      ],
    };

    await getMartinusMailer().send({ message, recipients: new Recipient({ to: recipients, replyTo: reporter.email }) });
    return true;
  },
});

export const getPersonsByNameAbbreviations = registerQuery({
  operationName: 'getPersonsByNameAbbreviations',
  middlewares: [authenticatedMiddleware],
  type: () => [Person],
  params: [{ type: () => [String], name: 'nameAbbreviations' }],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  resolve: (nameAbbreviations: string[]) => async (gqlFields: BaseQLSelectionSet<Person>) => {
    const col = await getCollection<Person>(Person._class);
    // the projection used to be passed as find options, so it never applied and full persons were read
    return col
      .find({ nameAbbreviation: { $in: nameAbbreviations } }, { projection: { nameAbbreviation: 1, _id: 1 } })
      .toArray();
  },
});

export const UserAPI = {
  create: createUser,
  resendInitMail,
  createPersonalNr: createPersonalNrForUser,
  reportProblem,
  getPersonsByNameAbbreviations,
};
